<template>
  <div class="deal-dashboard">
    <h1>{{ trans('dashboard.deals.title') }}</h1>
    <div class="deal-dashboard__header dd-header">
      <div class="dd-header__controls">
        <div class="dd-header__controls-left">
          <input-search
            class="dd-header__search"
            :placeholder="trans('user.inbox.searchbar.placeholder')"
            @search-input="onSearchInput"
          />
        </div>
        <div class="dd-header__controls-right ddhcr">
          <div class="input-checkbox">
            <input
              id="include-archive-deals-btn"
              v-model="archivedFilter"
              type="checkbox"
              class="ddhcr__archive"
              @change="onArchivedCheckboxChange"
            >
            <label for="include-archive-deals-btn">
              {{ trans('user.leads.display_archived') }}
            </label>
          </div>
        </div>
      </div>
    </div>
    <div class="deal-dashboard__content">
      <proposal-column
        v-if="proposals.length > 0"
        :proposals="proposals"
        class="deal-dashboard__col deal-dashboard__proposal-column"
        @proposal-refused="onProposalRefused"
      />
      <div v-for="state in states" :key="state" class="deal-dashboard__col">
        <spinner-container v-if="loading[state] || loading.all" />
        <column-header :title="trans(`deal.state.${state}`)" :counter="totalRecords[state]" />
        <div :ref="`column-${state}`" class="dd-col__content" :class="`dd-col__content--${state}`" :data-state="state">
          <dashboard-card
            v-for="deal in deals[state]"
            :key="deal.id"
            :meta="deal"
            :deal="deal"
            :states="states"
            :data-id="deal.id"
            :current-column="state"
          />
        </div>
      </div>
    </div>
    <card-actions-container
      :payload="cardActionPayload"
      @card-action-cancelled="onCardActionCancelled"
      @card-action-completed="onCardActionCompleted"
      @deal-card-to-feedback="onCardToFeedback"
    />
  </div>
</template>

<script>
import Sortable from 'sortablejs';
import { storeToRefs } from 'pinia';
import InputSearch from '@components/InputSearch.vue';
import SpinnerContainer from '@components/SpinnerContainer.vue';
import { useScreensizeStore } from '@stores/useScreensizeStore.ts';
import translation from '@elements/translation';
import requests from '@tools/requests-mixin';
import {
  URL_SELECTION_ITEM_REMOVE,
  URL_XHR_BUYER_DASHBOARD_PROPOSALS,
  URL_XHR_DEALS,
} from '@tools/routes/routes';
import eventBus from '@tools/event-bus';
import Logger from '@tools/logger/logger';
import { flashBagError } from '@tools/flashbag';
import DashboardCard from './card/Card.vue';
import ColumnHeader from './ColumnHeader.vue';
import CardActionsContainer from './card/CardActionsContainer.vue';
import ProposalColumn from './proposal/ProposalsColumn.vue';
import { COLUMN__SIGNED, COLUMN__UNSIGNED, COLUMNS, EVENTS } from './constants';
import CardActionPayload from './card/CardActionPayload';
import { ACTIONS } from './actions/constants';
import { OFFER_EVENT } from '../offer/constants';

// Used to match all columns with deal
const ALL_COLUMNS = 'all';

export default {
  components: {
    DashboardCard,
    ColumnHeader,
    InputSearch,
    CardActionsContainer,
    ProposalColumn,
    SpinnerContainer,
  },
  mixins: [ translation, requests ],
  setup() {
    const { isDesktop } = storeToRefs(useScreensizeStore());

    return {
      isDesktop,
    };
  },
  data() {
    return {
      currentPage: { in_progress: 1, visited: 1, signed: 1, unsigned: 1 },
      loading: { in_progress: false, visited: false, signed: false, unsigned: false, all: false },
      totalRecords: { in_progress: 0, visited: 0, signed: 0, unsigned: 0 },
      deals: { in_progress: [], visited: [], signed: [], unsigned: [] },
      states: COLUMNS,
      archivedFilter: false,
      searchString: '',
      searchTimeout: null,
      cardActionPayload: null,
      proposals: [],
    };
  },
  async mounted() {
    eventBus.addEventListener(EVENTS.CARD_MOVED, this.onCardMoved);
    eventBus.addEventListener(EVENTS.CARD_TO_ARCHIVE, this.onCardToArchive);
    eventBus.addEventListener(EVENTS.CARD_TO_FEEDBACK, this.onCardToFeedback);
    eventBus.addEventListener(EVENTS.CARD_ACTION_CANCELLED_VISIT, this.onCardCancelledVisit);
    this.resetDeals(ALL_COLUMNS);
    this.setDeals(window.initialDealDashboardDealsState);
    this.setProposals(window.initialDealDashboardProposalsState);
    if (this.isDesktop) {
      await this.createSortable();
    }
    eventBus.addEventListener(OFFER_EVENT.PROPOSAL_OFFER_REMOVE, this.removeProposal);
  },
  methods: {
    async removeProposal(event) {
      const { proposalId, refusalReasons = [] } = event.detail;

      try {
        await this.delete(URL_SELECTION_ITEM_REMOVE.replace('{id}', proposalId), JSON.stringify({ proposalId, refusalReasons }));
        eventBus.dispatchEvent(new CustomEvent(OFFER_EVENT.PROPOSAL_REFUSE, { detail: { proposalId } }));
      } catch (error) {
        flashBagError();
        Logger.error(error);
      }
    },
    async createSortable() {
      await this.$nextTick();
      this.states.forEach((state) => {
        const elementRef = this.$refs[`column-${state}`];
        if (elementRef && elementRef.length) {
          const element = elementRef[0];

          /* eslint-disable-next-line no-new */
          new Sortable(element, {
            group: 'shared',
            filter: '.dashboard-card--not-draggable',
            ghostClass: 'dashboard-card--ghost',
            onEnd: (event) => {
              this.onCardDropped(event);
            },
          });
        }
      });
    },
    async fetchDeals(state, page, force) {
      if (!force && Object.prototype.hasOwnProperty.call(this.deals, state) && this.deals[state].length >= this.totalRecords[state]) {
        // All deals are loaded
        return;
      }
      this.loading[state] = true;
      const result = await this.get(`${
        URL_XHR_DEALS
          .replace('{state}', state)
          .replace('{page}', page)
      }?${this.getSearchParameters().toString()}`);
      if (result.data) {
        if (Object.prototype.hasOwnProperty.call(this.currentPage, state)) {
          this.currentPage[state] = page;
        }
        this.setDeals(result.data);
      }
      this.loading[state] = false;
    },
    getLocalDealById(dealId) {
      let deal = null;
      Object.values(this.deals).forEach((arr) => {
        deal = deal || arr.find((d) => d.id === dealId);
      });
      return deal;
    },
    getSearchParameters() {
      const params = new URLSearchParams();
      if (this.searchString) {
        params.append('search', this.searchString);
      }
      if (this.archivedFilter) {
        params.append('archived', this.archivedFilter ? '1' : '0');
      }
      return params;
    },
    locateDealById(dealId) {
      let column = null;
      let index = null;
      for (const [ c, deals ] of Object.entries(this.deals)) { // eslint-disable-line no-restricted-syntax
        deals.forEach((d, i) => { // eslint-disable-line no-loop-func
          if (d.id === dealId) {
            column = c;
            index = i;
          }
        });
        if (column !== null && index !== null) {
          break;
        }
      }
      return [ column, index ];
    },
    onArchivedCheckboxChange() {
      this.refreshDeals(ALL_COLUMNS);
    },
    onColumnScroll(event, state) {
      const element = event.target;
      if (element.scrollHeight - element.scrollTop <= element.clientHeight) {
        this.fetchDeals(state, this.currentPage[state] + 1, false);
      }
    },
    onSearchInput(value) {
      if (this.searchTimeout !== null) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.searchString = value;
        this.refreshDeals(ALL_COLUMNS);
      }, 1000);
    },
    refreshDeals(columnToRefresh) {
      this.resetDeals(columnToRefresh);
      this.fetchDeals(columnToRefresh, 1, true);
    },
    resetDeals(columnToReset) {
      if (columnToReset === ALL_COLUMNS) {
        this.deals = { in_progress: [], visited: [], signed: [], unsigned: [] };
        this.totalRecords = { in_progress: 0, visited: 0, signed: 0, unsigned: 0 };
        this.currentPage = { in_progress: 1, visited: 1, signed: 1, unsigned: 1 };
        return;
      }
      this.deals[columnToReset] = [];
      this.totalRecords[columnToReset] = 0;
      this.currentPage[columnToReset] = 1;
    },
    setDeals(payload) {
      payload.forEach((dealsPerState) => {
        const dealState = dealsPerState.type;
        this.totalRecords[dealState] = dealsPerState.total_records;
        const ids = [];
        this.deals[dealState].forEach((deal) => {
          ids.push(deal.id);
        });
        dealsPerState.deals.forEach((deal) => {
          if (!ids.includes(deal.id)) {
            this.deals[dealState].push(deal);
          }
        });
      });
    },
    moveDeal(toColumn, toIndex, fromColumn, fromIndex) {
      if (fromColumn === toColumn) {
        return;
      }
      const bak = this.deals[fromColumn][fromIndex];
      // Remove deal from its previous column
      this.deals[fromColumn].splice(fromIndex, 1);
      // Insert deal at its new column
      this.deals[toColumn].splice(toIndex, 0, bak);
    },
    onCardDropped(event) {
      eventBus.dispatchEvent(new CustomEvent(EVENTS.CARD_MOVED, {
        detail: {
          dealId: parseInt(event.item.dataset.dealId, 10),
          to: event.to.dataset.state,
          from: event.from.dataset.state,
        },
      }));
    },
    onCardMoved(event) {
      const { dealId, to, from } = event.detail;

      // The card was "moved" without drag'n'drop (through mobile view)
      // We have to perform what the draggable lib does when a card is dropped.
      // Find the deal
      const index = this.deals[from].findIndex((d) => d.id === dealId);
      if (index === -1) {
        return;
      }
      // Move the deal to the first place (arbitrary position) in its new column
      this.moveDeal(to, 0, from, index);

      const payload = new CardActionPayload();
      payload.fromColumn = from;
      payload.fromColumnIndex = index;
      payload.toColumn = to;
      payload.deal = this.getLocalDealById(dealId);
      payload.interlocutor = payload.deal.supervisor;
      // This will trigger a watcher in <card-actions-container>
      this.cardActionPayload = payload;
    },
    onCardToArchive(event) {
      const { dealId } = event.detail;
      const payload = new CardActionPayload();
      [ payload.fromColumn, payload.fromColumnIndex ] = this.locateDealById(dealId.detail);
      payload.deal = this.getLocalDealById(dealId.detail);
      payload.interlocutor = payload.deal.supervisor;
      payload.action = [ COLUMN__SIGNED, COLUMN__UNSIGNED ].includes(payload.fromColumn)
        ? ACTIONS.DEAL__ARCHIVE : ACTIONS.DEAL__ARCHIVE_UI;
      // Trigger the action
      this.cardActionPayload = payload;
    },
    onCardToFeedback(dealId) {
      const payload = new CardActionPayload();
      [ payload.fromColumn, payload.fromColumnIndex ] = this.locateDealById(dealId.detail);
      payload.deal = this.getLocalDealById(dealId.detail);
      payload.interlocutor = payload.deal.supervisor;
      payload.action = ACTIONS.VISIT__FEEDBACK;
      // This will trigger a watcher in <card-actions-container>
      this.cardActionPayload = payload;
    },
    onCardToInProgress(dealId) {
      const payload = new CardActionPayload();
      [ payload.fromColumn, payload.fromColumnIndex ] = this.locateDealById(dealId);
      payload.deal = this.getLocalDealById(dealId);
      payload.interlocutor = payload.deal.supervisor;
      payload.action = ACTIONS.DEAL__IN_PROGRESS;
      // This will trigger a watcher in <card-actions-container>
      this.cardActionPayload = payload;
    },
    onCardCancelledVisit(dealId) {
      const payload = new CardActionPayload();
      [ payload.fromColumn, payload.fromColumnIndex ] = this.locateDealById(dealId.detail);
      payload.deal = this.getLocalDealById(dealId.detail);
      payload.interlocutor = payload.deal.supervisor;
      payload.action = ACTIONS.VISIT__CANCEL;
      // Trigger the action
      this.cardActionPayload = payload;
    },
    /**
     * @param {CardActionPayload} originalPayload
     */
    onCardActionCompleted(originalPayload) {
      this.resetCardActionPayload();
      if (!originalPayload.next) {
        if (originalPayload.action === ACTIONS.DEAL__ARCHIVE) {
          // Refresh all dashboard
          this.refreshDeals(ALL_COLUMNS);
          return;
        }
        // Refresh updated columns
        if (originalPayload.toColumn) {
          this.refreshDeals(originalPayload.toColumn);
        }
        if (originalPayload.fromColumn) {
          this.refreshDeals(originalPayload.fromColumn);
        }
        return;
      }

      // Locate deal and update payload
      /** @type {CardActionPayload} next */
      const { next } = originalPayload;
      [ next.fromColumn, next.fromColumnIndex ] = this.locateDealById(originalPayload.deal.id);
      if (next.toColumn) {
        // If a "to" column is specified, move the deal to keep the UI up-to-date.
        this.moveDeal(next.toColumn, 0, next.fromColumn, next.fromColumnIndex);
      }
      // Trigger the new action
      this.cardActionPayload = next;
    },
    /**
     * @param {CardActionPayload} toRevert
     */
    onCardActionCancelled(toRevert) {
      if (toRevert.fromColumn && toRevert.toColumn) {
        // Find the deal
        const index = this.deals[toRevert.toColumn].findIndex((d) => d.id === toRevert.deal.id);
        if (index > -1) {
          // Reintegrate the deal where it belongs
          // It's a revert, so from & to are inverted.
          this.moveDeal(toRevert.fromColumn, toRevert.fromColumnIndex, toRevert.toColumn, index);
        }
      }
      this.resetCardActionPayload();
    },
    resetCardActionPayload() {
      this.cardActionPayload = null;
    },
    async fetchProposals() {
      const result = await this.get(URL_XHR_BUYER_DASHBOARD_PROPOSALS);
      if (result.data) {
        this.setProposals(result.data);
      }
    },
    refreshProposals() {
      this.fetchProposals();
    },
    setProposals(proposals) {
      this.proposals = proposals;
    },
    onProposalRefused() {
      this.refreshProposals();
    },
  },
};
</script>
